import { useEffect, useState } from 'react';

/**
 * Theme controller. Light-first (per Mirfa "Light first, Dark optional"): the
 * default preference is `system`, which follows the OS but starts light on a
 * light OS. Users can pin `light` or `dark`. Applies the `.dark` class on
 * <html>, which the CSS token layer keys off.
 */
const THEME_KEY = 'mirfa-theme';
const listeners = new Set();

export function getStoredPreference() {
  try {
    const v = localStorage.getItem(THEME_KEY);
    if (v === 'light' || v === 'dark' || v === 'system') return v;
  } catch (_) {}
  return 'system';
}

function systemPrefersDark() {
  return typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(prefers-color-scheme: dark)').matches
    : false;
}

export function resolveTheme(preference = getStoredPreference()) {
  return preference === 'system' ? (systemPrefersDark() ? 'dark' : 'light') : preference;
}

function apply(preference) {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.toggle('dark', resolveTheme(preference) === 'dark');
}

export function setThemePreference(preference) {
  try {
    localStorage.setItem(THEME_KEY, preference);
  } catch (_) {}
  apply(preference);
  listeners.forEach((fn) => fn(preference));
}

/** Call once at startup (before render) to apply the stored theme + track the OS. */
export function initTheme() {
  apply(getStoredPreference());
  if (typeof window !== 'undefined' && window.matchMedia) {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      if (getStoredPreference() === 'system') apply('system');
    };
    mq.addEventListener?.('change', onChange);
  }
}

/** React hook: `{ preference, resolved, setPreference }`. */
export function useTheme() {
  const [preference, setPref] = useState(getStoredPreference);
  useEffect(() => {
    const fn = (p) => setPref(p);
    listeners.add(fn);
    return () => listeners.delete(fn);
  }, []);
  return {
    preference,
    resolved: resolveTheme(preference),
    setPreference: setThemePreference,
  };
}
